import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { APIResponse, BasicResponse } from '../_types/api-responses.types';
import { InsurerDto } from '../_types/billing.types';
import {
  GetCentralisationCompaniesRequest,
  GetCentralisationCompaniesResponse,
} from '../_types/centralisation.types';
import {
  AddProductRequest,
  CategoryDocument,
  CategoryDocumentCreationRequest,
  CategoryDocumentUpdateRequest,
  DeleteProductCommissionRequest,
  ExtendedProductDetails,
  GetCompanyContactsResponse,
  PatchCategoryRequest,
  Product,
  ProductCommission,
  UpdateProductRequest,
  UpdateSubcategoryRequest,
  UpsertProductCommissionRequest,
} from '../_types/product.types';
import { UserAuthService } from './user-auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  catalogueBaseUrl: string;

  baseHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  get headers() {
    return this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
  }

  constructor(
    private readonly httpClient: HttpClient,
    private readonly userAuthService: UserAuthService
  ) {
    this.catalogueBaseUrl = environment.CATALOGUEAPIURL;
  }

  addProductCommission(
    body: UpsertProductCommissionRequest
  ): Observable<APIResponse<BasicResponse>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );

    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}product/add/commission`,
      body,
      {
        headers,
      }
    );
  }

  deleteProductCommission(
    body: DeleteProductCommissionRequest
  ): Observable<APIResponse<BasicResponse>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );

    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}product/delete/commission`,
      body,
      {
        headers,
      }
    );
  }

  getProductCommissions(
    productId: string
  ): Observable<APIResponse<{ commissions: ProductCommission[] }>> {
    return this.httpClient.post<
      APIResponse<{ commissions: ProductCommission[] }>
    >(
      `${this.catalogueBaseUrl}product/get/commission`,
      { productId },
      {
        headers: this.headers,
      }
    );
  }

  searchInsurers(
    query: string,
    limit?: number
  ): Observable<APIResponse<{ insurers: InsurerDto[] }>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    const params: Record<string, string | number> = { query };
    if (limit != null) params['limit'] = limit;
    return this.httpClient.get<APIResponse<{ insurers: InsurerDto[] }>>(
      `${this.catalogueBaseUrl}category/insurer/search`,
      { headers, params }
    );
  }

  /**
   * Every insurance company with the addresses it publishes, for the "Email
   * compagnie" picker.
   *
   * `searchBy` is left out on purpose: the server matches it against the English
   * `category` column only, accent-sensitively, and orders by creation date — so
   * the whole list is fetched once and filtered client-side instead.
   */
  getCompanyContacts(): Observable<APIResponse<GetCompanyContactsResponse>> {
    return this.httpClient.post<APIResponse<GetCompanyContactsResponse>>(
      `${this.catalogueBaseUrl}category/get/company-contacts`,
      {},
      { headers: this.headers }
    );
  }

  getAllCategoriesProducts(): Observable<
    APIResponse<{ categoryList: ExtendedProductDetails[] }>
  > {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );

    return this.httpClient.post<
      APIResponse<{ categoryList: ExtendedProductDetails[] }>
    >(
      `${this.catalogueBaseUrl}category/get/all/details`,
      {},
      {
        headers,
      }
    );
  }

  addProduct(obj: AddProductRequest): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}product/add`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  updateProduct(
    obj: UpdateProductRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}product/update`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  getProductDetails(obj: {
    productId: string;
  }): Observable<APIResponse<Product>> {
    return this.httpClient.post<APIResponse<Product>>(
      `${this.catalogueBaseUrl}product/get`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  deleteProduct(obj: {
    productIds: string[];
  }): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}product/delete`,
      obj,
      { headers: this.headers }
    );
  }

  patchProduct(
    obj: { productId: string } & Partial<UpdateProductRequest>
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}product/patch`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  getCategoryDocuments(
    categoryId: string,
    activeOnly: boolean = false
  ): Observable<APIResponse<{ documents: CategoryDocument[] }>> {
    return this.httpClient.post<APIResponse<{ documents: CategoryDocument[] }>>(
      `${this.catalogueBaseUrl}category-condition-document/get`,
      { categoryId, activeOnly },
      { headers: this.headers }
    );
  }

  addCategoryDocument(
    obj: CategoryDocumentCreationRequest
  ): Observable<APIResponse<BasicResponse>> {
    const form = new FormData();
    form.append('categoryId', obj.categoryId);
    form.append('documentName', obj.documentName);
    form.append('file', obj.file);

    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}category-condition-document/create`,
      form,
      {
        headers: new HttpHeaders().set(
          'Access-Token',
          this.userAuthService.getCurrentUser().token
        ),
      }
    );
  }

  updateCategoryDocument(
    obj: CategoryDocumentUpdateRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}category-condition-document/update`,
      obj,
      { headers: this.headers }
    );
  }

  deleteCategoryDocument(id: number): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}category-condition-document/delete`,
      { id },
      { headers: this.headers }
    );
  }

  /**
   * Updates only the fields given; anything left out keeps its stored value. The company profile
   * saves one card at a time, and a full update would make each card resend - and therefore have to
   * fill in - the fields owned by the others. Send an empty string to clear a field.
   */
  patchCategory(
    categoryId: string,
    body: PatchCategoryRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.patch<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}category/${categoryId}`,
      body,
      { headers: this.headers }
    );
  }

  updateSubcategory(
    obj: UpdateSubcategoryRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}category/update/subcategory`,
      obj,
      { headers: this.headers }
    );
  }

  deleteCategories(
    categoryIds: string[]
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this.catalogueBaseUrl}category/delete`,
      { categoryIds },
      { headers: this.headers }
    );
  }

  getCentralisationCompanies(
    obj: GetCentralisationCompaniesRequest
  ): Observable<APIResponse<GetCentralisationCompaniesResponse>> {
    return this.httpClient.post<
      APIResponse<GetCentralisationCompaniesResponse>
    >(`${this.catalogueBaseUrl}category/get/centralisation-companies`, obj, {
      headers: this.headers,
    });
  }
}
